import React, {useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {Box, Text, Radio, Stack, useStyleConfig} from '@chakra-ui/react'
import {
    ADD_FILTER,
    REMOVE_FILTER,
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'
import {remCalc} from '../../../theme/_utils'

const RadioRefinement = ({
    filter,
    value,
    toggleFilter,
    isSelected,
    idx,
    focusedIdx,
    setFocusedIdx,
    itemRefs,
    onClose,
}) => {
    const styles = useStyleConfig('RadioRefinements')
    const {formatMessage} = useIntl()

    const handleToggle = () => toggleFilter(value, filter.attributeId, filter.label, false, false)

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            e.preventDefault()
            const next = (idx + 1) % filter.values.length
            setFocusedIdx(next)
            itemRefs.current[next]?.focus()
            if (idx + 1 === filter.values.length) {
                onClose()
            }
        }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            const next = (idx + 1) % filter.values.length
            setFocusedIdx(next)
            itemRefs.current[next]?.focus()
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            const prev = (idx - 1 + filter.values.length) % filter.values.length
            setFocusedIdx(prev)
            itemRefs.current[prev]?.focus()
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleToggle()
        }
    }

    return (
        <Box
            as="div"
            role="menuitem"
            tabIndex={focusedIdx === idx ? 0 : -1}
            ref={(el) => (itemRefs.current[idx] = el)}
            onFocus={() => setFocusedIdx(idx)}
            onClick={(e) => {
                if (e.target.type !== 'radio') {
                    handleToggle()
                }
            }}
            onKeyDown={handleKeyDown}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            cursor="pointer"
            _focusVisible={{boxShadow: 'outline', bg: 'none'}}
            _hover={{bg: 'none'}}
            _focus={{bg: 'none'}}
            {...styles?.stackItem}
            aria-label={formatMessage(isSelected ? REMOVE_FILTER : ADD_FILTER, value)}
        >
            <Box {...styles?.textContainer}>
                <Text fontSize="sm">{value.label}</Text>
                <Text {...styles?.filterHit} aria-hidden="true">
                    {formatMessage(
                        {
                            id: 'radioRefinements.hitCount',
                            defaultMessage: '({radioRefinementsHitCount})',
                        },
                        {radioRefinementsHitCount: value.hitCount},
                    )}
                </Text>
            </Box>
            <Radio
                display="inline-flex"
                height={{base: '44px', lg: '24px'}}
                borderWidth={remCalc(0.6)}
                isChecked={isSelected}
                onChange={handleToggle}
                inputProps={{'aria-hidden': true, tabIndex: -1}}
            />
        </Box>
    )
}

RadioRefinement.propTypes = {
    filter: PropTypes.object,
    value: PropTypes.object,
    toggleFilter: PropTypes.func,
    isSelected: PropTypes.bool,
    idx: PropTypes.number,
    focusedIdx: PropTypes.number,
    setFocusedIdx: PropTypes.func,
    itemRefs: PropTypes.object,
    onClose: PropTypes.func,
}

const RadioRefinements = ({filter, toggleFilter, selectedFilters, onClose}) => {
    const styles = useStyleConfig('RadioRefinements')
    const [focusedIdx, setFocusedIdx] = useState(0)
    const itemRefs = useRef([])

    return (
        <Stack {...styles?.stack}>
            {filter.values.map((value, idx) => {
                const isSelected = selectedFilters.includes(value.value)
                if (value.hitCount === 0 && !isSelected) return null

                return (
                    <RadioRefinement
                        key={value.value}
                        value={value}
                        filter={filter}
                        toggleFilter={toggleFilter}
                        isSelected={isSelected}
                        idx={idx}
                        focusedIdx={focusedIdx}
                        setFocusedIdx={setFocusedIdx}
                        itemRefs={itemRefs}
                        onClose={onClose}
                    />
                )
            })}
        </Stack>
    )
}

RadioRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array,
    onClose: PropTypes.func,
}

export default RadioRefinements
